import {PropsWithChildren, useContext, useEffect, useMemo, useState} from 'react'
import {COLUMNS, compareArrays} from '../../../common/Helpers'
import {AllFundCatalogContext} from '../AllFundCatalogContext'
import {SessionContext, SessionContextType} from '../../../common/ctx/SessionContext'

export const AllFundCatalogContextProvider = (props: PropsWithChildren) => {
    const STORAGE_KEY = `all-settings:${process.env.REACT_APP_BUILD}`
    const sessionCtx: SessionContextType = useContext(SessionContext)

    const defaults = {
        favorites: [],
        compared: [],
        kinds: [],
        columns: COLUMNS,
        sortCol: 'name',
        sortDir: 'ASC',
        limit: 20,
    }

    const settings = useMemo(() => {
        return JSON.parse('' + localStorage.getItem(STORAGE_KEY))
    }, [])

    const [favorites, setFavorites] = useState<Array<string>>(defaults.favorites)
    const [compared, setCompared] = useState<Array<string>>(defaults.compared)
    const [kinds, setKinds] = useState<Array<string>>(defaults.kinds)
    const [columns, setColumns] = useState<Array<string>>(defaults.columns)
    const [sortCol, setSortCol] = useState<string>(defaults.sortCol)
    const [sortDir, setSortDir] = useState<string>(defaults.sortDir)
    const [limit, setLimit] = useState<number>(defaults.limit)
    const [activeFilters, setActiveFilters] = useState<object>({})
    const [date, setDate] = useState<Date | null>(null)
    const [minDate, setMinDate] = useState<Date | null>(null)
    const [maxDate, setMaxDate] = useState<Date | null>(null)
    const [today, setToday] = useState<Date | null>(null)
    const [holiday, setHoliday] = useState<Array<string>>([])
    const [showFavoritesInfoBox, setShowFavoritesInfoBox] = useState<boolean>(false)

    const setDefault = () => {
        setKinds(defaults.kinds)
    }

    useEffect(() => {
        if (settings) {
            setFavorites(settings.favorites)
            setKinds(settings.kinds)
            setCompared(settings.compared)
            setColumns(settings.columns)
            setSortCol(settings.sortCol)
            setSortDir(settings.sortDir)
            setLimit(settings.limit)
        }
    }, [settings])

    useEffect(() => {
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                favorites: favorites,
                kinds: kinds,
                compared: compared,
                columns: columns,
                sortCol: sortCol,
                sortDir: sortDir,
                limit: limit,
            })
        )
    }, [favorites, kinds, columns, sortCol, sortDir, limit])

    const isDefault = (): boolean => {
        const data = JSON.parse('' + localStorage.getItem(STORAGE_KEY))
        if (data === null) {
            sessionCtx.setItem(STORAGE_KEY, 'hidden')
            return true
        }

        if (
            data?.sortCol === defaults.sortCol &&
            data?.sortDir === defaults.sortDir &&
            data?.limit === defaults.limit &&
            compareArrays(data?.kinds, defaults.kinds) &&
            compareArrays(data?.favorites, defaults.favorites) &&
            compareArrays(data?.columns, defaults.columns)
        ) {
            sessionCtx.setItem(STORAGE_KEY, 'hidden')
            return true
        }

        return false
    }

    const setItem = (key: string, value: string): void => {
        localStorage.setItem(key, value)
    }

    const getItem = (key: string): string | null => {
        return localStorage.getItem(key)
    }

    return (
        <AllFundCatalogContext.Provider
            value={{
                STORAGE_KEY,
                getItem,
                setItem,
                isDefault,
                favorites,
                setFavorites,
                showFavoritesInfoBox,
                setShowFavoritesInfoBox,
                compared,
                setCompared,
                sortCol,
                setSortCol,
                sortDir,
                setSortDir,
                limit,
                setLimit,
                columns,
                setColumns,
                kinds,
                setKinds,
                date,
                setDate,
                minDate,
                setMinDate,
                maxDate,
                setMaxDate,
                today,
                setToday,
                activeFilters,
                setActiveFilters,
                holiday,
                setHoliday,
                setDefault,
            }}>
            {props.children}
        </AllFundCatalogContext.Provider>
    )
}
